(function () {
  'use strict';

  // ── CONFIG ─────────────────────────────────────────────────
  const PHX_CHECKOUT_URL = "https://secureorder.trywellvi.com";
  const PHX_ROUTING_MODE = "all";
  const PHX_PRODUCT_ALLOWLIST = new Set([
    // '1234567890',
  ]);

  // Buy Now behavior (applies to REAL buy-now buttons only):
  //   'variant_only' — checkout with only the current variant at its cart qty (default)
  //   'full_cart'    — checkout with the entire cart as-is
  //   'single'       — clear cart, add 1 of this variant, checkout
  const PHX_BUY_NOW_MODE = 'variant_only';

  // ── CONSTANTS ───────────────────────────────────────────────
  const LOG_PREFIX = '[PHX]';
  const CART_FETCH_TIMEOUT = 5000;
  const SDK_URL = 'https://io.ecommcheckout.com/phoenix.min.js';
  const BUSY_TIMEOUT_MS = 8000;
  const BUSY_SWEEP_MS = 2000;

  // ── STATE ───────────────────────────────────────────────────
  let redirectInProgress = false;
  let upcartObserverAttached = false;
  let hooksScheduled = false;

  // ── LOGGING ─────────────────────────────────────────────────
  const log = {
    info: (...args) => console.log(LOG_PREFIX, ...args),
    warn: (...args) => console.warn(LOG_PREFIX, ...args),
    error: (...args) => console.error(LOG_PREFIX, ...args),
    debug: (...args) => {
      if (sessionStorage.getItem('phx_debug') === '1') {
        console.debug(LOG_PREFIX, ...args);
      }
    },
  };

  window.__PHX_CHECKOUT_URL = PHX_CHECKOUT_URL;

  // ── PHOENIX SDK LOADER ──────────────────────────────────────
  function loadPhoenixSDK() {
    const s = document.createElement('script');
    s.src = SDK_URL;
    document.head.appendChild(s);

    window.addEventListener('load', function () {
      if ('phx' in window) {
        window.phx.initialize(window);
        log.info('SDK initialized');
        return;
      }
      let tries = 0;
      const interval = setInterval(function () {
        if ('phx' in window || tries > 20) {
          clearInterval(interval);
          if ('phx' in window) {
            window.phx.initialize(window);
            log.info('SDK initialized (deferred)');
          } else {
            log.warn('SDK failed to load after 10s');
          }
        }
        tries++;
      }, 500);
    });
  }

  // ── HELPERS ─────────────────────────────────────────────────
  function shopID() {
    return window.Shopify?.shop?.split('.')?.[0] || '';
  }

  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  async function fetchWithTimeout(url, opts = {}, timeout = CART_FETCH_TIMEOUT) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const res = await fetch(url, { ...opts, signal: controller.signal });
      clearTimeout(timer);
      return res;
    } catch (err) {
      clearTimeout(timer);
      throw err;
    }
  }

  async function getCart() {
    try {
      const res = await fetchWithTimeout('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`cart.js ${res.status}`);
      return await res.json();
    } catch (err) {
      log.error('Failed to fetch cart:', err);
      return { items: [], token: '' };
    }
  }

  function normalizeToken(t) {
    return (t || '').replace('%3Fkey%3D', '?key=');
  }

  // ── STUCK-BUTTON RECOVERY ───────────────────────────────────
  function resetPhxButtons(force) {
    document.querySelectorAll('[data-phx-busy="1"]').forEach((btn) => {
      const since = parseInt(btn.dataset.phxBusySince || '0', 10);
      if (!force && Date.now() - since < BUSY_TIMEOUT_MS) return;
      btn.dataset.phxBusy = '0';
      btn.removeAttribute('data-phx-busy-since');
      btn.classList.remove('button-loading');
      btn.disabled = false;
      log.debug('Released stuck button', btn);
    });
    if (force) redirectInProgress = false;
  }
  window.__phxReset = () => resetPhxButtons(true);

  function markBusy(el) {
    el.dataset.phxBusy = '1';
    el.dataset.phxBusySince = String(Date.now());
    el.classList.add('button-loading');
    el.disabled = true;
  }

  function clearBusy(el) {
    el.dataset.phxBusy = '0';
    el.removeAttribute('data-phx-busy-since');
    el.classList.remove('button-loading');
    el.disabled = false;
  }

  // ── QUANTITY BREAK HELPER ───────────────────────────────────
  function getQtyBreakItems() {
    const checkedRadio = document.querySelector(
      '.lumin-product-block-qty-break input[type="radio"]:checked'
    );
    if (!checkedRadio?.value) return null;
    const ids = checkedRadio.value.split(',').filter(Boolean);
    if (ids.length <= 1) return null;
    const map = {};
    ids.forEach((id) => { map[id] = (map[id] || 0) + 1; });
    return Object.entries(map).map(([id, quantity]) => ({ id, quantity }));
  }

  // ── PRODUCT ROUTING ─────────────────────────────────────────
  async function shouldRedirectCart() {
    if (PHX_ROUTING_MODE === 'all') return true;
    const cart = await getCart();
    if (!cart.items?.length) return false;
    return cart.items.some((item) =>
      PHX_PRODUCT_ALLOWLIST.has(String(item.product_id))
    );
  }

  // ── QUERY PARAM PERSISTENCE ─────────────────────────────────
  function persistQueryParams() {
    if (sessionStorage.getItem('phx_query_params')) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.toString()) return;
    const all = {};
    for (const [key, value] of params.entries()) all[key] = value;
    sessionStorage.setItem('phx_query_params', JSON.stringify(all));
    log.debug('Persisted query params:', all);
  }

  // ── TRAFFIC SOURCE ──────────────────────────────────────────
  function captureTrafficSource() {
    const params = new URLSearchParams(window.location.search);
    if (params.has('trafficSource')) return;

    const referrer = document.referrer;
    if (!referrer) return;

    try {
      const referrerHost = new URL(referrer).hostname;
      const currentHost = window.location.hostname;

      if (referrerHost !== currentHost) {
        params.set('trafficSource', referrerHost);
      } else {
        const ts = new URL(referrer).searchParams.get('trafficSource');
        if (ts) params.set('trafficSource', ts);
      }

      window.history.replaceState(
        {},
        '',
        window.location.pathname + '?' + params.toString()
      );
    } catch {
      // invalid referrer URL, ignore
    }
  }

  // ── BUILD CHECKOUT URL ──────────────────────────────────────
  async function buildCheckoutURL(cartToken) {
    const dest = new URL(PHX_CHECKOUT_URL);

    const cart = await getCart();
    const token = cartToken
      || normalizeToken(cart.token)
      || normalizeToken(getCookie('cart'));

    dest.searchParams.set('store', shopID());
    dest.searchParams.set('cart', token);

    if (window.token) {
      dest.searchParams.set('event-token', window.token);
    }

    // Currency conversion (BucksCc integration)
    const customerCurrency = sessionStorage.getItem('buckscc_customer_currency');
    const ratesString = sessionStorage.getItem('bucksccConversionRates');
    if (customerCurrency && ratesString) {
      const currency = customerCurrency.replace(/"/g, '');
      dest.searchParams.set('target_currency', currency);
      try {
        const rates = JSON.parse(ratesString);
        if (rates.currencyRateToday) {
          const match = rates.currencyRateToday.match(/rates:\s*(\{[^}]+\})/);
          if (match) {
            const rateObj = JSON.parse(match[1]);
            if (rateObj[currency]) {
              dest.searchParams.set(
                'cc_rate',
                (1 / parseFloat(rateObj[currency])).toFixed(6)
              );
            }
          }
        }
      } catch (err) {
        log.warn('Currency rate parse error:', err);
      }
    }

    // Traffic source
    try {
      const ts = new URLSearchParams(window.location.search).get('trafficSource') || '';
      if (ts) dest.searchParams.set('trafficSource', ts);
    } catch {}

    // Persisted query params (UTM, etc.)
    try {
      const stored = JSON.parse(sessionStorage.getItem('phx_query_params') || '{}');
      for (const [key, value] of Object.entries(stored)) {
        if (!dest.searchParams.has(key)) dest.searchParams.set(key, value);
      }
    } catch (err) {
      log.warn('Query param restore error:', err);
    }

    // Intelligems params — from cart attributes, fallback to cookies
    try {
      const attrs = cart.attributes || {};
      const igParamsObj = {};

      if (attrs.igId) igParamsObj.igId = attrs.igId;
      if (attrs.igPreview) igParamsObj.igPreview = attrs.igPreview;
      if (attrs.igTestGroups) igParamsObj.igTestGroups = attrs.igTestGroups;

      if (Object.keys(igParamsObj).length === 0) {
        const igId = getCookie('ig-id');
        const igPre = getCookie('igPreview');
        const igTg = getCookie('igTestGroups');
        if (igId) igParamsObj.igId = igId;
        if (igPre) igParamsObj.igPreview = igPre;
        if (igTg) igParamsObj.igTestGroups = igTg;
      }

      if (Object.keys(igParamsObj).length > 0) {
        dest.searchParams.append('igParams', btoa(JSON.stringify(igParamsObj)));
      }
    } catch (err) {
      log.warn('Intelligems param error:', err);
    }

    // Intelligems SDK assignments
    if (window.intelligems && typeof window.intelligems.getAllAssignments === 'function') {
      try {
        const assignments = window.intelligems.getAllAssignments();
        for (const [key, value] of Object.entries(assignments)) {
          dest.searchParams.append(key, value);
        }
      } catch (err) {
        log.warn('Intelligems assignments error:', err);
      }
    }

    return dest.toString();
  }

  // ── SDK EVENT TRACKING ──────────────────────────────────────
  function trackCheckoutLoad() {
    try {
      if (window.phx?.track) {
        window.phx.track({ eventType: 'checkout_load', eventFrom: 'shopify' });
        log.debug('Tracked checkout_load event');
      }
    } catch (err) {
      log.warn('Failed to track checkout_load:', err);
    }
  }

  function trackFailedRedirect() {
    try {
      if (window.phx?.track) {
        window.phx.track({ eventType: 'failed_to_redirect', eventFrom: 'shopify' });
      }
    } catch {}
  }

  // ── DRAWER CONTROL ──────────────────────────────────────────
  function openDrawer() {
    if (typeof window.upcartOpenCart === 'function') {
      try {
        // window.upcartOpenCart();
        log.debug('UpCart drawer opened');
        return true;
      } catch (e) {
        log.warn('upcartOpenCart failed', e);
      }
    }
    return false;
  }

  // Ask cart apps / theme to re-read /cart.js so the drawer shows the new item
  function notifyCartUpdated() {
    ['cart:refresh', 'cart:updated', 'upcart:cart:updated'].forEach((name) => {
      try {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
      } catch {}
    });
  }

  // ── REDIRECT EXECUTION (checkout buttons only) ──────────────
  async function redirectToPhoenix(cartToken) {
    if (redirectInProgress) {
      log.warn('Redirect already in progress — resetting flag and retrying');
      redirectInProgress = false;
    }

    redirectInProgress = true;
    log.info('Redirecting to Phoenix checkout...');

    trackCheckoutLoad();

    let redirectSucceeded = false;
    window.addEventListener('beforeunload', () => { redirectSucceeded = true; }, { once: true });
    setTimeout(() => {
      if (!redirectSucceeded) {
        trackFailedRedirect();
        resetPhxButtons(true);
      }
    }, 3000);

    try {
      const url = await buildCheckoutURL(cartToken);
      log.info('Destination:', url);
      window.location.href = url;
    } catch (err) {
      log.error('Redirect failed:', err);
      redirectInProgress = false;
      resetPhxButtons(true);
      openDrawer();
    }
  }

  async function handleCartRedirect(cartToken) {
    const allowed = await shouldRedirectCart();
    if (allowed) {
      await redirectToPhoenix(cartToken);
    } else {
      log.info('Cart products not in allowlist, using native checkout');
      window.location.href = '/checkout';
    }
  }

  // ===========================================================
  //  PRIMARY: Navigation API Intercept
  // ===========================================================
  function setupNavigationIntercept() {
    if (typeof navigation === 'undefined') return false;

    navigation.addEventListener('navigate', (event) => {
      let url;
      try {
        url = new URL(event.destination.url);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin) return;

      const pathname = url.pathname;

      log.debug(
        'navigate:', pathname,
        '| type:', event.navigationType,
        '| formData:', !!event.formData
      );

      if (
        pathname.includes('/checkout') ||
        pathname.includes('/checkouts') ||
        pathname.includes('/cn/') ||
        pathname.startsWith('/cart/c/') ||
        ((pathname === '/cart' || pathname === '/cart/') && event.formData)
      ) {
        log.info('Intercepted checkout navigation:', pathname);
        event.preventDefault();
        event.stopImmediatePropagation();

        redirectInProgress = false;

        const tokenMatch = pathname.match(/^\/cart\/c\/([^/]+)/);
        if (tokenMatch) {
          log.info('Buy Now token detected:', tokenMatch[1]);
          handleCartRedirect(tokenMatch[1]);
        } else {
          handleCartRedirect();
        }
      }
    });

    log.info('Navigation API intercept active');
    return true;
  }

  // ===========================================================
  //  SELECTORS
  // ===========================================================

  // Add to Cart → add item, open the UpCart drawer. NEVER redirects.
  const ADD_TO_CART_SELECTORS = [
    'button.gp-button-atc[aria-label="Add to cart"]',
    // add any other ATC buttons here
  ];

  // Real Buy Now → straight to Phoenix checkout.
  const BUY_NOW_SELECTORS = [
    '.product__buy-now',
    '[data-action="buy-now"]',
    '[data-action="checkout"]',
    '[data-replo-checkout-button]',
    '[data-pf-type="ProductCheckout"]',
    '[data-gem-action="buy-now"]',
    '[data-bold-checkout]',
    '[data-rebuy-buy-now]',
    '[data-rebuy-checkout]',
    'shopify-buy-it-now-button',
    '.shopify-payment-button__button',
    '.ocu-buy-now-button',
    '.custom-buy-now',
    '.zipify-checkout-button',
    'button[data-checkout="checkout"]'
  ];

  // Cart / drawer checkout buttons → straight to Phoenix checkout.
  const CART_CHECKOUT_SELECTORS = [
    'button[name="checkout"]',
    'button#checkout',
    '.cart__checkout-button',
    '#rebuy-cart .rebuy-button.rebuy-cart__checkout-button',
    '.ocu-checkout-button',
    '.cuatom_btn',
    '.custom-checkout',
    '[form="ocu-cart-form"][name="checkout"]',
    '#kaching-cart__checkout-button',
    '.kaching-cart__checkout-button',
    '.AOV-CartDrawer-Checkout',
    '.hs-checkout-purchase',
    '[data-instant-layout="CART"] a[rel="noopener noreferrer"]',
    '#slidecart-checkout-form',
    '.custom_checkout'
  ];

  // ── CONTEXT HELPERS ─────────────────────────────────────────
  function findClosest(el, selector) {
    while (el && el !== document) {
      if (el.matches?.(selector)) return el;
      el = el.parentNode || el.host;
    }
    return null;
  }

  function getVariantFromContext(el) {
    const form = findClosest(el, 'form[action*="/cart"], form[action="/cart/add"], form');
    const variantId =
      form?.querySelector('input[name="id"], select[name="id"]')?.value ||
      window.ShopifyAnalytics?.meta?.selectedVariantId ||
      null;
    const qtyRaw =
      form?.querySelector('input[name="quantity"]')?.value ||
      findClosest(el, '.product, .product-single, [data-section-type="product"], main')
        ?.querySelector('input[name="quantity"]')?.value ||
      document.querySelector('input[name="quantity"]')?.value ||
      '1';
    return { variantId, qty: parseInt(qtyRaw, 10) || 1 };
  }

  // ── ADD TO CART HANDLER (drawer, no redirect) ───────────────
  async function addToCartAndOpenDrawer(el, event) {
    if (el.dataset.phxBusy === '1') return;
    markBusy(el);
    event?.preventDefault?.();
    event?.stopImmediatePropagation?.();

    try {
      const { variantId, qty } = getVariantFromContext(el);
      const qtyBreakItems = getQtyBreakItems();

      if (qtyBreakItems) {
        await fetchWithTimeout('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: qtyBreakItems }),
        });
        log.info('Add to cart: qty break items', qtyBreakItems);
      } else if (variantId) {
        await fetchWithTimeout('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] }),
        });
        log.info('Add to cart: variant', variantId, 'qty', qty);
      } else {
        log.warn('Add to cart: no variant found, opening drawer anyway');
      }

      notifyCartUpdated();
      openDrawer();
    } catch (err) {
      log.error('Add to cart failed:', err);
      openDrawer();
    } finally {
      clearBusy(el);
    }
  }

  // ── BUY NOW HANDLER (redirects) ─────────────────────────────
  async function buyNowAndRedirect(el, event) {
    if (el.dataset.phxBusy === '1') return;
    markBusy(el);
    event?.preventDefault?.();
    event?.stopImmediatePropagation?.();

    try {
      if (PHX_BUY_NOW_MODE === 'full_cart') {
        await handleCartRedirect();
        return;
      }

      const { variantId } = getVariantFromContext(el);
      const qtyBreakItems = getQtyBreakItems();

      if (PHX_BUY_NOW_MODE === 'single') {
        await fetchWithTimeout('/cart/clear.js', { method: 'POST' });
        if (qtyBreakItems) {
          await fetchWithTimeout('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: qtyBreakItems }),
          });
        } else if (variantId) {
          await fetchWithTimeout('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
          });
        }
        await handleCartRedirect();
        return;
      }

      // 'variant_only' mode
      if (!variantId && !qtyBreakItems) {
        log.warn('Buy Now: no variant ID found, redirecting with full cart');
        await handleCartRedirect();
        return;
      }

      await fetchWithTimeout('/cart/clear.js', { method: 'POST' });

      if (qtyBreakItems) {
        await fetchWithTimeout('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: qtyBreakItems }),
        });
        log.info('Buy Now: qty break items', qtyBreakItems);
      } else {
        const qty = parseInt(
          document.querySelector('input[name="quantity"]')?.value ||
          document.querySelector('.quantity__input')?.value ||
          document.querySelector('[data-quantity-input]')?.value ||
          document.querySelector('.qty__input')?.value ||
          document.querySelector('[data-quantity]')?.value || '1', 10) || 1;

        await fetchWithTimeout('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] }),
        });

        log.info('Buy Now: variant', variantId, 'qty', qty);
      }

      await handleCartRedirect();
    } catch (err) {
      log.error('Buy Now failed:', err);
    } finally {
      clearBusy(el);
    }
  }

  // ── BUTTON HOOKING ───────────────────────────────────────────
  // NOTE: 'id' is deliberately NOT stripped — removing it broke the theme's
  // and the cart app's own JS (button#checkout, #slidecart-checkout-form...).
  const ATTRS_TO_REMOVE = ['onclick', 'form', 'action', 'name', 'method', 'data-ocu-checkout'];

  function hookButtons(selectors, handler) {
    function stripAttrs(btn) {
      ATTRS_TO_REMOVE.forEach((attr) => btn.removeAttribute(attr));
      if ('type' in btn) btn.type = 'button';
    }

    function watchAttrs(btn) {
      const observer = new MutationObserver(() => {
        const needsStrip = ATTRS_TO_REMOVE.some((attr) => btn.hasAttribute(attr));
        if (needsStrip) {
          observer.disconnect();
          stripAttrs(btn);
          observer.observe(btn, { attributes: true });
        }
      });
      observer.observe(btn, { attributes: true });
    }

    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((btn) => {
        if (btn._phxBound) return;
        btn._phxBound = true;
        stripAttrs(btn);
        watchAttrs(btn);
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          handler(btn, e);
        }, true);
      });
    });
  }

  // ── UPCART DRAWER ────────────────────────────────────────────
  // The observer is attached exactly ONCE. Previously a new document-wide
  // observer was created on every hook pass, which froze the page after the
  // drawer was opened and closed.
  function bindUpcartCheckout() {
    const link = document.querySelector('.upcart-checkout-button');
    if (!link || link._phxBound) return;
    link._phxBound = true;
    link.removeAttribute('href');
    link.style.cursor = 'pointer';
    log.debug('UpCart checkout button prepared');
  }

  function hookUpcartDrawer() {
    bindUpcartCheckout();
    if (upcartObserverAttached) return;
    upcartObserverAttached = true;
    new MutationObserver(bindUpcartCheckout).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
    log.debug('UpCart observer attached (once)');
  }

  // Single delegated handler for the UpCart checkout button (works in shadow DOM)
  document.addEventListener('click', async (e) => {
    const button = e.composedPath().find(
      (el) => el?.classList?.contains('upcart-checkout-button')
    );
    if (!button) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    log.info('UpCart Checkout clicked');
    try {
      await handleCartRedirect();
    } catch (err) {
      log.error('UpCart redirect error:', err);
      resetPhxButtons(true);
    }
  }, true);

  // ── OCU CART CHECKOUT ────────────────────────────────────────
  function hookOCUCartCheckout() {
    const SELECTOR = 'button.ocu-checkout-button, [form="ocu-cart-form"][name="checkout"]';
    const EVENTS = ['pointerdown', 'mousedown', 'click', 'touchstart'];

    const handler = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (e.type !== 'click') return; // act once per interaction
      log.info('OCU checkout intercepted');
      await handleCartRedirect();
    };

    document.querySelectorAll(SELECTOR).forEach((btn) => {
      if (btn.__phxHardBound) return;
      btn.__phxHardBound = true;
      EVENTS.forEach((evt) => btn.addEventListener(evt, handler, true));
      ['onclick', 'form'].forEach((attr) => btn.removeAttribute(attr));
      if ('type' in btn) btn.type = 'button';
    });
  }

  // ── MONSTER CART DRAWER ──────────────────────────────────────
  function hookMonsterCartDrawer() {
    const btn = document.getElementById('mu-checkout-button');
    if (!btn || btn._phxBound) return;
    btn._phxBound = true;
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      await handleCartRedirect();
    }, true);
  }

  // ── ALL HOOKS ───────────────────────────────────────────────
  function runAllHooks() {
    hookButtons(ADD_TO_CART_SELECTORS, addToCartAndOpenDrawer);
    hookButtons(BUY_NOW_SELECTORS, buyNowAndRedirect);
    hookButtons(CART_CHECKOUT_SELECTORS, () => handleCartRedirect());
    hookUpcartDrawer();
    hookOCUCartCheckout();
    hookMonsterCartDrawer();
  }

  // Coalesce mutation bursts into one pass per frame
  function scheduleHooks() {
    if (hooksScheduled) return;
    hooksScheduled = true;
    requestAnimationFrame(() => {
      hooksScheduled = false;
      runAllHooks();
    });
  }

  function setupHooks() {
    new MutationObserver(scheduleHooks).observe(document.body, {
      childList: true,
      subtree: true,
    });
    runAllHooks();
  }

  // ===========================================================
  //  INIT
  // ===========================================================
  setupNavigationIntercept();

  function init() {
    log.info('v2.3 | mode:', PHX_ROUTING_MODE, '| store:', shopID());

    loadPhoenixSDK();
    persistQueryParams();
    captureTrafficSource();

    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        resetPhxButtons(true);
        runAllHooks();
      }
    });

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible' && !redirectInProgress) {
        resetPhxButtons(false);
      }
    });

    setInterval(() => resetPhxButtons(false), BUSY_SWEEP_MS);

    if (document.body) {
      setupHooks();
    } else {
      document.addEventListener('DOMContentLoaded', setupHooks);
    }
  }

  window.redirectToPhoenix = redirectToPhoenix;
  window.__phxOpenDrawer = openDrawer;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();